import { useEffect, useMemo, useState } from 'react';
import type { PaigeFilingType, PaigeJobFilingPlan } from './paige.global';

/** Field kinds that hold a table, not one value, so no destination can take them as one property or column. */
const tableKinds = new Set(['line-items', 'table']);

/**
 * The job's document types with their fields, as Paige last read them from the job's setup (the
 * job's file naming carries them); empty until read, or without a Paige job.
 */
export function useJobTypes(): PaigeFilingType[] {
  const [types, setTypes] = useState<PaigeFilingType[]>([]);
  useEffect(() => {
    const channel = window.paigeWorkbench?.filingPlan;
    if (!channel) return;
    let live = true;
    channel
      .read()
      .then((read) => live && setTypes(read.types))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return types;
}

/**
 * The job's field names, every document type's, once each in the order the setup lists them, for a
 * field map's "Paige field" picker; empty until read, or without a Paige job, when the map takes typed
 * names instead.
 */
export function useJobFieldNames(): string[] {
  const types = useJobTypes();
  return useMemo(() => fieldNamesOf({ types }), [types]);
}

/** The distinct field names of the job's types, tables left out. */
export function fieldNamesOf(read: Pick<PaigeJobFilingPlan, 'types'>): string[] {
  const seen = new Map<string, string>();
  for (const type of read.types)
    for (const field of type.fields)
      if (!tableKinds.has(field.kind) && !seen.has(field.name.toLowerCase()))
        seen.set(field.name.toLowerCase(), field.name);
  return [...seen.values()];
}
